import { message } from 'antd';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type AudiCalculationModuleSpecsFragment } from '../../../api/fragments';
// eslint-disable-next-line max-len
import { useUpdateAudiCalculationFinanceProductsMutation } from '../../../api/mutations/updateAudiCalculationFinanceProducts';
// eslint-disable-next-line max-len
import { useUpdateAudiCalculationInsuranceProductsMutation } from '../../../api/mutations/updateAudiCalculationInsuranceProducts';
import Form from '../../../components/fields/Form';
import useHandleError from '../../../utilities/useHandleError';
import AudiCalculationManagementSetting, { type FormValues } from './AudiCalculationManagementSetting';

type AudiCalculationManagementProps = {
    module: AudiCalculationModuleSpecsFragment;
};

type AudiCalculationManagementInnerProps = {
    module: AudiCalculationModuleSpecsFragment;
};

const AudiCalculationManagementInner = ({ module }: AudiCalculationManagementInnerProps) => {
    const { t } = useTranslation('audiCalculationDetails');
    const [financeMutation] = useUpdateAudiCalculationFinanceProductsMutation();
    const [insuranceMutation] = useUpdateAudiCalculationInsuranceProductsMutation();

    const initialValues = useMemo<FormValues>(
        () => ({
            settings:
                module.audiCalculationSettings?.map(item => ({
                    id: item.id,
                    financeProductSuiteIds: item.financeProductSuiteIds,
                    insuranceProductSuiteIds: item.insuranceProductSuiteIds,
                })) ?? [],
        }),
        [module]
    );

    const onSubmit = useHandleError<FormValues>(
        async values => {
            const { settings } = values;

            message.loading({
                content: t('audiCalculationDetails:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            // for each of the settings, call API mutation
            settings.map(async setting => {
                const { financeProductSuiteIds, insuranceProductSuiteIds } = setting;

                await financeMutation({
                    variables: { moduleId: module.id, financeProductSuiteIds, settingId: setting.id },
                });

                if (insuranceProductSuiteIds) {
                    await insuranceMutation({
                        variables: { moduleId: module.id, insuranceProductSuiteIds, settingId: setting.id },
                    });
                }
            });

            message.destroy('primary');
            message.success({
                content: t('audiCalculationDetails:messages.updateSuccessful'),
                key: 'primary',
            });
        },
        [financeMutation, insuranceMutation, module.id, t]
    );

    const formId = `audiCalculationDetails-${module.id}`;

    return (
        <Formik<FormValues> initialValues={initialValues} onSubmit={onSubmit}>
            {({ handleSubmit }) => (
                <Form id={formId} name={formId} onSubmitCapture={handleSubmit}>
                    <AudiCalculationManagementSetting module={module} name="settings" />
                </Form>
            )}
        </Formik>
    );
};

const AudiCalculationManagement = ({ module }: AudiCalculationManagementProps) => (
    <AudiCalculationManagementInner module={module} />
);

export default AudiCalculationManagement;
